/**
 * *
 * * Component Name: App
 * Project: ecp
 * Description: Entry point to the application being used in index root
 * Author: Pranatosh Roy
 * Date Created: 18-April-2022
 * Last Modified On
 * Remarks:
 */

/** ------------------ Primary Import Declaration -------- */
import React, {
  useEffect,
  useContext,
  useState,
  useReducer,
  useMemo,
  useCallback,
  useRef,
} from "react";
import moment from "moment";
/** ------------------ MUI Themes ------------------------ */
import { ThemeProvider } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import useMediaQuery from "@mui/material/useMediaQuery";
import GlobalStyles from "@mui/material/GlobalStyles";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
/** ------------------ App Route Component --------------- */
import PreSetup from "./Errors/PreSetup.jsx";
import AppRoutes from "AppRoutes.jsx";
import CartTransferManager from "components/CartTransferManager/CartTransferManager.jsx";
import AuthDebugger from "components/AuthDebugger/AuthDebugger.jsx";
/** ------------------ Theming and Styling --------------- */
import { darkTheme, lightTheme } from "theme.js";
import { ThemeContext } from "context/ThemeContext";
import { AuthContext, reducer } from "context/Authentication";
import { SnackbarProvider } from "context/SnackbarContext";
import { initGuestCartDB, getAllGuestCartItems } from "utils/guestCartDB.js";
import { encryptText, decryptText } from "commonmethods/Encryption.js";
import { postRecord } from "services/services";
/** ------------------ Style Components ------------------ */
import globalStyles from "globalassets/styles/globalStyles";
/** ------------------ User Components ------------------- */
/** ------------------ API Declarations ------------------ */
/** ------------------ Third Party Components ------------ */
const API_GETCOMPANYINFO = "companyinfo/getbyid";
const API_GETTHEMEINFO = "themeinfo/getbyid";
const API_GETAPPSETTINGS = "appsettings/getall";
const defaultColor = "#f44336";
export default function App(props) {
  /** ------------------ Initial States for authentication -------------------- */
  const initialState = {
    isAuthenticated: false,
    user: null,
    token: null,
    guestCart: [],
    guestCartCount: 0,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  /** ------------------ useStates ------------------------- */
  const [loading, setLoading] = useState(true);
  const [appReady, setAppReady] = useState(false); // New state for progressive loading
  const [primaryColor, setPrimaryColor] = React.useState(defaultColor);
  const [secondaryColor, setSecondaryColor] = React.useState(defaultColor);
  const [AlternativeColor, setAlternativeColor] = React.useState(defaultColor);
  const [companyInfoData, setCompanyInfoData] = React.useState({});
  const [okSetup, setOkSetup] = React.useState(true);
  const [backgroundImage, setBackgroundImage] = useState("");
  const [appError, setAppError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [dataLoaded, setDataLoaded] = useState({
    company: false,
    theme: false,
    settings: false,
  });
  const [loadingProgress, setLoadingProgress] = useState({
    company: "pending",
    settings: "pending",
    percentage: 0,
  });

  // Refs for stable function references
  const initializeAppDataRef = useRef();
  const verifyAuthRef = useRef();
  const abortControllerRef = useRef();
  /** ------------------ Functions & Events ---------------- */
  /** ------------------ MediaQuery ------------------------- */
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  /** ------------------ useContext ------------------------- */
  const themeMode = useContext(ThemeContext);
  const darkMode = themeMode.state.darkMode;
  const dynamicThemes = React.useMemo(() => {
    const commonPalette = {
      primary: { main: primaryColor },
      secondary: { main: secondaryColor },
    };

    return {
      dark: createTheme({
        ...darkTheme,
        palette: {
          ...darkTheme.palette,
          ...commonPalette,
        },
      }),
      light: createTheme({
        ...lightTheme,
        palette: {
          ...lightTheme.palette,
          ...commonPalette,
        },
      }),
    };
  }, [primaryColor, secondaryColor]);
  /** ------------------ useEffects ------------------------ */

  // Helper function for reliable cache validation
  const isCacheValid = useCallback((timestamp, cacheType = null) => {
    if (!timestamp) return false;

    try {
      const now = new Date();
      const cacheTime = new Date(timestamp);
      const diffMinutes = (now - cacheTime) / (1000 * 60);

      // Determine cache duration based on type
      let cacheDurationMinutes;
      switch (cacheType) {
        case "appSettings":
          cacheDurationMinutes = 60; // 60 minutes
          break;
        case "companyInfo":
          cacheDurationMinutes = 43200; // 30 days in minutes
          break;
        default:
          cacheDurationMinutes = 60; // default 30 minutes for backward compatibility
      }

      return diffMinutes <= cacheDurationMinutes && !isNaN(diffMinutes);
    } catch (error) {
      return false;
    }
  }, []);

  // Helper function to handle legacy cache data without timestamps
  const handleLegacyCache = useCallback((data, cacheKey) => {
    if (!data) return null;

    // Handle different data structures
    let hasValidStructure = false;
    if (cacheKey === "companyInfo") {
      hasValidStructure = data.Company_Id;
    } else if (cacheKey === "appSettings") {
      hasValidStructure =
        Array.isArray(data) || (data && typeof data === "object");
    }

    // If data exists but no RefreshStamp, it's legacy cache
    if (!data.RefreshStamp && hasValidStructure) {
      // Add timestamp to legacy data and re-cache it
      const timestampedData = {
        ...data,
        RefreshStamp: new Date().toISOString(),
      };

      // Re-save with timestamp
      localStorage.setItem(
        cacheKey,
        encryptText(JSON.stringify(timestampedData))
      );

      return timestampedData;
    }

    return data;
  }, []);

  // Update refs with current functions
  useEffect(() => {
    initializeAppDataRef.current = initializeAppData;
    verifyAuthRef.current = verifyAuth;
  });

  // Add ref to prevent double initialization with simplified tracking
  const initializationRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization
    if (initializationRef.current) {
      return;
    }

    initializationRef.current = true;

    // Create new abort controller for this effect
    abortControllerRef.current = new AbortController();

    // Only set theme mode on mount, not on every prefersDarkMode change
    const savedDarkMode = localStorage.getItem("darkMode");
    if (savedDarkMode === null) {
      // Only use system preference if no saved preference
      if (prefersDarkMode) {
        themeMode.dispatch({ type: "DARKMODE" });
      } else {
        themeMode.dispatch({ type: "LIGHTMODE" });
      }
    } else if (savedDarkMode === "true") {
      themeMode.dispatch({ type: "DARKMODE" });
    } else {
      themeMode.dispatch({ type: "LIGHTMODE" });
    }

    // Initialize app with stable function references
    const initialize = async () => {
      try {
        setAppError(false);
        setRetrying(false);

        // CRITICAL: Verify auth IMMEDIATELY to prevent redirect issues on refresh
        verifyAuthRef.current?.();

        // Then: Initialize app data (company info, theme, settings) in parallel
        await initializeAppDataRef.current?.(
          abortControllerRef.current?.signal
        );

        // Initialize guest cart from IndexedDB
        try {
          await initGuestCartDB();
          const guestCartItems = await getAllGuestCartItems();
          if (guestCartItems && guestCartItems.length > 0) {
            dispatch({
              type: "LOAD_GUEST_CART",
              payload: {
                items: guestCartItems,
                count: guestCartItems.length,
              },
            });
          }
        } catch (error) {
          console.error("Error initializing guest cart:", error);
          // Don't fail app initialization if guest cart fails
        }
      } catch (error) {
        if (!abortControllerRef.current?.signal?.aborted) {
          setAppError(true);
        }
      }
    };

    initialize();

    return () => {
      abortControllerRef.current?.abort();
      // Reset flag on cleanup to allow re-initialization if needed
      initializationRef.current = false;
    };
  }, []); // Remove prefersDarkMode dependency

  // Helper function to load cached data immediately on app start
  const loadCachedDataImmediately = useCallback(async () => {
    try {
      // Check if data already exists in context (from previous session)
      const hasCompanyInContext =
        state.companyInfo && state.companyInfo.Company_Id;
      const hasSettingsInContext =
        state.appSettings && Array.isArray(state.appSettings);

      if (hasCompanyInContext && hasSettingsInContext) {
        setDataLoaded({ company: true, theme: true, settings: true });
        return true; // Return true to indicate data is loaded
      }

      // Try to load from localStorage cache
      let loadedFromCache = false;
      let companyLoaded = hasCompanyInContext;
      let settingsLoaded = hasSettingsInContext;

      // Load company data from cache if not in context
      if (!hasCompanyInContext) {
        try {
          const companybytes =
            localStorage.getItem("companyInfo") &&
            decryptText(localStorage.getItem("companyInfo"));
          let companyInfo = companybytes && JSON.parse(companybytes);

          // Handle legacy cache data without timestamps
          companyInfo = handleLegacyCache(companyInfo, "companyInfo");

          if (
            companyInfo &&
            companyInfo.Company_Id &&
            isCacheValid(companyInfo.RefreshStamp, "companyInfo")
          ) {
            setOkSetup(true);
            setPrimaryColor(companyInfo.Primary_Back_Color || defaultColor);
            setSecondaryColor(companyInfo.Secondary_Back_Color || defaultColor);
            setGraphics(companyInfo?.Graphics_Back_Image);
            dispatch({
              type: "SETBRAND",
              payload: { companyInfo },
            });
            setDataLoaded((prev) => ({ ...prev, company: true, theme: true }));
            loadedFromCache = true;
            companyLoaded = true;
          } else if (companyInfo && companyInfo.Company_Id) {
            // Cache exists but expired
          }
        } catch (error) {
          // Error loading company cache
        }
      } else {
        setDataLoaded((prev) => ({ ...prev, company: true, theme: true }));
      }

      // Load settings data from cache if not in context
      if (!hasSettingsInContext) {
        try {
          const settingsByte =
            localStorage.getItem("appSettings") &&
            decryptText(localStorage.getItem("appSettings"));
          let appSettings = settingsByte && JSON.parse(settingsByte);

          // Handle legacy cache data without timestamps
          appSettings = handleLegacyCache(appSettings, "appSettings");

          // Check if we have valid settings data
          const hasValidSettings =
            appSettings &&
            (Array.isArray(appSettings) ||
              (appSettings.RefreshStamp && typeof appSettings === "object"));

          if (
            hasValidSettings &&
            isCacheValid(appSettings.RefreshStamp, "appSettings")
          ) {
            // Ensure proper array format for context
            let settingsForContext = appSettings;
            if (!Array.isArray(appSettings) && appSettings.RefreshStamp) {
              // Extract array from timestamped object if needed
              settingsForContext = Array.isArray(appSettings)
                ? appSettings
                : Object.values(appSettings).filter(
                    (v) => v !== appSettings.RefreshStamp
                  );
            }

            dispatch({
              type: "SETSETTINGS",
              payload: { appSettings: settingsForContext },
            });
            setDataLoaded((prev) => ({ ...prev, settings: true }));
            loadedFromCache = true;
            settingsLoaded = true;
          } else if (appSettings) {
            // Settings cache exists but expired
          }
        } catch (error) {
          // Error loading settings cache
        }
      } else {
        setDataLoaded((prev) => ({ ...prev, settings: true }));
      }

      if (companyLoaded && settingsLoaded) {
        return true; // All data available, no API calls needed
      } else if (loadedFromCache) {
        return false; // Some data loaded, but might need API calls for missing data
      }

      return false; // No valid cache found, API calls needed
    } catch (error) {
      return false;
    }
  }, [state.companyInfo, state.appSettings, isCacheValid]);

  // Add separate effect for dark mode preference changes:
  useEffect(() => {
    const savedDarkMode = localStorage.getItem("darkMode");
    if (savedDarkMode === null) {
      // Only update if no saved preference exists
      if (prefersDarkMode) {
        themeMode.dispatch({ type: "DARKMODE" });
      } else {
        themeMode.dispatch({ type: "LIGHTMODE" });
      }
    }
  }, [prefersDarkMode]);

  // Effect to handle loading state based on data completion
  useEffect(() => {
    // Check if we have all critical data from context or localStorage
    const hasCompanyInContext =
      state.companyInfo && state.companyInfo.Company_Id;
    const hasSettingsInContext =
      state.appSettings && Array.isArray(state.appSettings);

    // Check localStorage as fallback
    const companybytes =
      localStorage.getItem("companyInfo") &&
      decryptText(localStorage.getItem("companyInfo"));
    let companyInfo = companybytes && JSON.parse(companybytes);
    companyInfo = handleLegacyCache(companyInfo, "companyInfo");
    const hasValidCompanyCache =
      companyInfo &&
      companyInfo.Company_Id &&
      isCacheValid(companyInfo.RefreshStamp, "companyInfo");

    const settingsByte =
      localStorage.getItem("appSettings") &&
      decryptText(localStorage.getItem("appSettings"));
    let appSettings = settingsByte && JSON.parse(settingsByte);
    appSettings = handleLegacyCache(appSettings, "appSettings");
    const hasValidSettingsCache =
      appSettings &&
      Array.isArray(appSettings) &&
      isCacheValid(appSettings.RefreshStamp, "appSettings");

    const allDataLoaded =
      dataLoaded.company && dataLoaded.theme && dataLoaded.settings;
    const hasAllRequiredData =
      (hasCompanyInContext || hasValidCompanyCache) &&
      (hasSettingsInContext || hasValidSettingsCache);

    // Calculate loading progress
    let completedItems = 0;
    let totalItems = 2; // company (includes theme) + settings

    if (dataLoaded.company && dataLoaded.theme) completedItems++;
    if (dataLoaded.settings) completedItems++;

    const percentage = (completedItems / totalItems) * 100;
    setLoadingProgress((prev) => ({ ...prev, percentage }));

    // If we have all required data (from context or valid cache), show app immediately
    if (hasAllRequiredData && !appReady) {
      setAppReady(true);
      setLoading(false);
      setDataLoaded({ company: true, theme: true, settings: true });
      return;
    }

    // Enable progressive loading after company data (critical for theme)
    if (dataLoaded.company && dataLoaded.theme && !appReady) {
      setAppReady(true);
      setTimeout(() => {
        setLoading(false);
      }, 100); // Reduced delay for faster loading
    } else if (allDataLoaded && !appError) {
      setTimeout(() => {
        setLoading(false);
      }, 50); // Final loading completion
    } else if (appError) {
      // Show error state instead of infinite loading
      setTimeout(() => {
        setLoading(false);
        setAppReady(true); // Allow app to load with fallback data
      }, 100);
    }
  }, [
    dataLoaded,
    appError,
    appReady,
    state.companyInfo,
    state.appSettings,
    isCacheValid,
  ]);

  const verifyAuth = useCallback(() => {
    var userbytes =
      localStorage.getItem("user") && decryptText(localStorage.getItem("user"));
    var user = userbytes ? JSON.parse(userbytes) : null;
    var accessbytes =
      localStorage.getItem("permissions") &&
      decryptText(localStorage.getItem("permissions"));

    var token = localStorage.getItem("token") && localStorage.getItem("token");
    var permissions = accessbytes ? JSON.parse(accessbytes) : null;

    if (user && permissions) {
      dispatch({
        type: "LOGIN",
        payload: {
          user,
          permissions,
          token,
        },
      });
      return { user, permissions, token };
    } else {
      dispatch({ type: "LOGOUT" });
      return null;
    }
  }, []);

  // Optimized parallel data initialization with smart caching
  const initializeAppData = useCallback(
    async (signal) => {
      try {
        // Check if request was aborted
        if (signal?.aborted) {
          throw new Error("Request aborted");
        }

        // First, try to load cached data immediately for faster startup
        const allDataLoadedFromCache = await loadCachedDataImmediately();

        // If all data is available from cache/context, skip API calls
        if (allDataLoadedFromCache) {
          return;
        }

        // Check if we already have valid data in context
        const hasCompanyInContext =
          state.companyInfo && state.companyInfo.Company_Id;
        const hasSettingsInContext =
          state.appSettings && Array.isArray(state.appSettings);

        // If we have all data in context, no need for API calls
        if (hasCompanyInContext && hasSettingsInContext) {
          setDataLoaded({ company: true, theme: true, settings: true });
          setAppError(false);
          setRetrying(false);
          return;
        }

        // Check cache and decide what needs to be fetched
        const needsCompanyData =
          !hasCompanyInContext && !(await checkCompanyCache());
        const needsSettingsData =
          !hasSettingsInContext && !(await checkSettingsCache());

        // If no API calls needed, mark everything as loaded
        if (!needsCompanyData && !needsSettingsData) {
          setDataLoaded({ company: true, theme: true, settings: true });
          setAppError(false);
          setRetrying(false);
          return;
        }

        // Only make API calls for missing/expired data
        const apiPromises = [];
        if (needsCompanyData) {
          apiPromises.push(fetchCompanyAndThemeData(signal));
        } else {
          // Use existing data
          setDataLoaded((prev) => ({ ...prev, company: true, theme: true }));
        }

        if (needsSettingsData) {
          apiPromises.push(fetchAppSettings(signal));
        } else {
          // Use existing data
          setDataLoaded((prev) => ({ ...prev, settings: true }));
        }

        // Execute only necessary API calls
        if (apiPromises.length > 0) {
          const results = await Promise.allSettled(apiPromises);

          let allSuccessful = true;
          results.forEach((result, index) => {
            if (result.status === "rejected") {
              allSuccessful = false;
            }
          });

          if (!allSuccessful) {
            throw new Error("Some API calls failed");
          }
        }

        setAppError(false);
        setRetrying(false);
      } catch (error) {
        if (signal?.aborted || error.message === "Request aborted") {
          return;
        }

        setAppError(true);
        setRetrying(false);

        // Fallback - try to use any available cached data
        await loadFallbackData();
      }
    },
    [state.companyInfo, state.appSettings]
  );

  // Helper function to check company cache validity
  const checkCompanyCache = useCallback(async () => {
    try {
      // First check if data is already in context
      if (state.companyInfo && state.companyInfo.Company_Id) {
        setDataLoaded((prev) => ({ ...prev, company: true, theme: true }));
        return true;
      }

      const companybytes =
        localStorage.getItem("companyInfo") &&
        decryptText(localStorage.getItem("companyInfo"));
      let companyInfo = companybytes && JSON.parse(companybytes);

      // Handle legacy cache data without timestamps
      companyInfo = handleLegacyCache(companyInfo, "companyInfo");

      if (
        companyInfo &&
        companyInfo.Company_Id &&
        isCacheValid(companyInfo.RefreshStamp, "companyInfo")
      ) {
        // Apply cached data immediately
        setOkSetup(true);
        setPrimaryColor(companyInfo.Primary_Back_Color || defaultColor);
        setSecondaryColor(companyInfo.Secondary_Back_Color || defaultColor);
        setGraphics(companyInfo?.Graphics_Back_Image);

        // Update context
        dispatch({
          type: "SETBRAND",
          payload: { companyInfo },
        });

        setDataLoaded((prev) => ({ ...prev, company: true, theme: true }));
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }, [state.companyInfo, isCacheValid, handleLegacyCache]);

  // Helper function to check settings cache validity
  const checkSettingsCache = useCallback(async () => {
    try {
      // First check if data is already in context
      if (state.appSettings && Array.isArray(state.appSettings)) {
        setDataLoaded((prev) => ({ ...prev, settings: true }));
        return true;
      }

      const settingsByte =
        localStorage.getItem("appSettings") &&
        decryptText(localStorage.getItem("appSettings"));
      let appSettings = settingsByte && JSON.parse(settingsByte);

      // Handle legacy cache data without timestamps
      appSettings = handleLegacyCache(appSettings, "appSettings");

      // Check if we have valid settings data structure
      const hasValidSettings =
        appSettings &&
        (Array.isArray(appSettings) ||
          (appSettings.RefreshStamp &&
            (Array.isArray(appSettings) || typeof appSettings === "object")));

      if (
        hasValidSettings &&
        isCacheValid(appSettings.RefreshStamp, "appSettings")
      ) {
        // Ensure proper array format for context
        let settingsForContext = appSettings;
        if (!Array.isArray(appSettings) && appSettings.RefreshStamp) {
          // Extract array from timestamped object if needed
          settingsForContext = Array.isArray(appSettings)
            ? appSettings
            : Object.values(appSettings).filter(
                (v) => v !== appSettings.RefreshStamp
              );
        }

        // Update context
        dispatch({
          type: "SETSETTINGS",
          payload: { appSettings: settingsForContext },
        });

        setDataLoaded((prev) => ({ ...prev, settings: true }));
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }, [state.appSettings, isCacheValid, handleLegacyCache]);

  // Background refresh effect for app settings
  useEffect(() => {
    // Don't start background refresh until app is fully loaded
    if (!appReady || appError) {
      return;
    }

    const checkAndRefreshAppSettings = async () => {
      try {
        // Check if app settings cache is expired
        const settingsByte =
          localStorage.getItem("appSettings") &&
          decryptText(localStorage.getItem("appSettings"));
        let appSettings = settingsByte && JSON.parse(settingsByte);

        if (appSettings && appSettings.RefreshStamp) {
          // Check if cache is expired (older than 60 minutes)
          if (!isCacheValid(appSettings.RefreshStamp, "appSettings")) {
            console.log(
              "🔄 App settings cache expired, refreshing in background..."
            );

            // Create abort controller for this background fetch
            const backgroundAbortController = new AbortController();

            // Fetch fresh app settings silently in background
            await fetchAppSettings(backgroundAbortController.signal);

            console.log("✅ App settings refreshed successfully in background");
          }
        }
      } catch (error) {
        // Silent error handling - don't disrupt user experience
        console.log(
          "⚠️ Background app settings refresh failed:",
          error.message
        );
      }
    };

    // Check every minute (60000ms) for expired cache
    const intervalId = setInterval(checkAndRefreshAppSettings, 60000);

    // Also check when user returns to the tab/window
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // User returned to tab, check if settings need refresh
        checkAndRefreshAppSettings();
      }
    };

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial check after 30 seconds to catch early expiration
    const initialCheckTimeout = setTimeout(checkAndRefreshAppSettings, 30000);

    // Cleanup interval and listeners on unmount
    return () => {
      clearInterval(intervalId);
      clearTimeout(initialCheckTimeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [appReady, appError, isCacheValid, fetchAppSettings]);

  // Fallback data loading function
  const loadFallbackData = useCallback(async () => {
    try {
      // Try to load any available cached data, even if expired
      const companybytes =
        localStorage.getItem("companyInfo") &&
        decryptText(localStorage.getItem("companyInfo"));
      const companyInfo = companybytes && JSON.parse(companybytes);

      const settingsByte =
        localStorage.getItem("appSettings") &&
        decryptText(localStorage.getItem("appSettings"));
      const appSettings = settingsByte && JSON.parse(settingsByte);

      if (companyInfo) {
        setOkSetup(true);
        setPrimaryColor(companyInfo.Primary_Back_Color || defaultColor);
        setSecondaryColor(companyInfo.Secondary_Back_Color || defaultColor);
        setGraphics(companyInfo?.Graphics_Back_Image);

        if (!state.companyInfo) {
          dispatch({
            type: "SETBRAND",
            payload: { companyInfo },
          });
        }
        setDataLoaded((prev) => ({ ...prev, company: true, theme: true }));
      }

      if (appSettings) {
        if (!state.appSettings) {
          dispatch({
            type: "SETSETTINGS",
            payload: { appSettings },
          });
        }
        setDataLoaded((prev) => ({ ...prev, settings: true }));
      }

      // If no cached data available, use defaults
      if (!companyInfo && !appSettings) {
        setOkSetup(true);
        setDataLoaded({ company: true, theme: true, settings: true });
      }
    } catch (error) {
      // Use absolute fallback
      setOkSetup(true);
      setDataLoaded({ company: true, theme: true, settings: true });
    }
  }, [state.companyInfo, state.appSettings]);

  // Optimized company data handler
  const handleCompanyData = useCallback(
    async (signal) => {
      try {
        // Check context first - if data exists, use it
        if (state.companyInfo && state.companyInfo.Company_Id) {
          console.log("Company data already in context");
          setDataLoaded((prev) => ({ ...prev, company: true, theme: true }));
          return true;
        }

        // Check cache second
        const companybytes =
          localStorage.getItem("companyInfo") &&
          decryptText(localStorage.getItem("companyInfo"));
        const companyInfo = companybytes && JSON.parse(companybytes);

        if (
          companyInfo &&
          isCacheValid(companyInfo.RefreshStamp, "companyInfo")
        ) {
          console.log("Using cached company data");
          // Use cached data
          setOkSetup(true);
          setPrimaryColor(companyInfo.Primary_Back_Color || defaultColor);
          setSecondaryColor(companyInfo.Secondary_Back_Color || defaultColor);
          setGraphics(companyInfo?.Graphics_Back_Image);
          dispatch({
            type: "SETBRAND",
            payload: { companyInfo },
          });
          setDataLoaded((prev) => ({ ...prev, company: true, theme: true }));
          return true;
        }

        console.log("Fetching fresh company data from API");
        // Fetch fresh data with parallel API calls
        return await fetchCompanyAndThemeData(signal);
      } catch (error) {
        console.error("Handle company data error:", error);
        return false;
      }
    },
    [state.companyInfo, isCacheValid]
  );

  // Optimized app settings handler
  const handleAppSettings = useCallback(
    async (signal) => {
      try {
        // Check context first - if data exists, use it
        if (state.appSettings && Array.isArray(state.appSettings)) {
          console.log("App settings already in context");
          setDataLoaded((prev) => ({ ...prev, settings: true }));
          return true;
        }

        // Check cache second
        const settingsByte =
          localStorage.getItem("appSettings") &&
          decryptText(localStorage.getItem("appSettings"));
        const appSettings = settingsByte && JSON.parse(settingsByte);

        if (
          appSettings &&
          isCacheValid(appSettings.RefreshStamp, "appSettings")
        ) {
          console.log("Using cached settings data");
          // Use cached data
          setOkSetup(true);
          dispatch({
            type: "SETSETTINGS",
            payload: { appSettings },
          });
          setDataLoaded((prev) => ({ ...prev, settings: true }));
          return true;
        }

        console.log("Fetching fresh settings data from API");
        // Fetch fresh data
        return await fetchAppSettings(signal);
      } catch (error) {
        console.error("Handle app settings error:", error);
        return false;
      }
    },
    [state.appSettings, isCacheValid]
  );

  // Parallel API calls for company and theme data
  const fetchCompanyAndThemeData = useCallback(async (signal) => {
    try {
      if (signal?.aborted) {
        throw new Error("Request aborted");
      }

      // Add timeout handling without changing service signature
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), 30000);
      });

      const [companyResponse, themeResponse] = await Promise.race([
        Promise.all([
          postRecord(API_GETCOMPANYINFO, { Company_Id: 1 }),
          postRecord(API_GETTHEMEINFO, { Theme_Id: 1 }),
        ]),
        timeoutPromise,
      ]);

      if (signal?.aborted) {
        throw new Error("Request aborted");
      }

      if (
        companyResponse.status === 200 &&
        companyResponse.data &&
        companyResponse.data.Company_Id
      ) {
        if (companyResponse.data.License_Status === "Active") {
          setOkSetup(true);

          // Combine company and theme data
          let companyThemeInfo = companyResponse.data;

          if (
            themeResponse.status === 200 &&
            themeResponse.data &&
            themeResponse.data.Theme_Id
          ) {
            companyThemeInfo = {
              ...companyResponse.data,
              ...themeResponse.data,
            };
          }

          // Update UI state
          setPrimaryColor(companyThemeInfo.Primary_Back_Color || defaultColor);
          setSecondaryColor(
            companyThemeInfo.Secondary_Back_Color || defaultColor
          );
          setGraphics(companyThemeInfo?.Graphics_Back_Image);

          // Cache the data with reliable timestamp
          const timestampedData = {
            ...companyThemeInfo,
            RefreshStamp: new Date().toISOString(),
          };
          localStorage.setItem(
            "companyInfo",
            encryptText(JSON.stringify(timestampedData))
          );

          dispatch({
            type: "SETBRAND",
            payload: { companyInfo: companyThemeInfo },
          });

          // Mark as loaded
          setDataLoaded((prev) => ({ ...prev, company: true, theme: true }));
          return true;
        } else {
          setOkSetup(false);
          return false;
        }
      } else {
        setOkSetup(false);
        return false;
      }
    } catch (error) {
      if (signal?.aborted || error.message === "Request aborted") {
        console.log("Company/theme fetch aborted");
        return false;
      }
      console.error("Error fetching company/theme data:", error);
      setOkSetup(false);
      return false;
    }
  }, []);

  // Optimized app settings fetch
  const fetchAppSettings = useCallback(async (signal) => {
    try {
      if (signal?.aborted) {
        throw new Error("Request aborted");
      }

      // Add timeout handling without changing service signature
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), 30000);
      });

      const response = await Promise.race([
        postRecord(API_GETAPPSETTINGS, { limit: 100, page: 1 }),
        timeoutPromise,
      ]);

      if (signal?.aborted) {
        throw new Error("Request aborted");
      }

      if (response.status === 200 && response.data) {
        let appSettings = response.data;

        // Ensure appSettings is an array (some APIs return objects)
        if (!Array.isArray(appSettings)) {
          console.log("Converting settings object to array format");
          appSettings = Array.isArray(appSettings)
            ? appSettings
            : [appSettings];
        }

        // Cache the data with reliable timestamp
        const timestampedData = {
          ...appSettings,
          RefreshStamp: new Date().toISOString(),
        };

        console.log("💾 Caching settings data:", timestampedData);
        localStorage.setItem(
          "appSettings",
          encryptText(JSON.stringify(timestampedData))
        );

        dispatch({
          type: "SETSETTINGS",
          payload: { appSettings },
        });

        // Mark as loaded
        setDataLoaded((prev) => ({ ...prev, settings: true }));
        return true;
      } else {
        console.error("Invalid settings response:", response);
        return false;
      }
    } catch (error) {
      if (signal?.aborted || error.message === "Request aborted") {
        console.log("App settings fetch aborted");
        return false;
      }
      console.error("Error fetching app settings:", error);
      return false;
    }
  }, []);

  const verifyCompanyInfo = () => {
    // Legacy function - now handled by initializeAppData
    // Keeping for backward compatibility if needed
  };

  const verifyAppSettings = () => {
    // Legacy function - now handled by initializeAppData
    // Keeping for backward compatibility if needed
  };

  const setCompanyInfo = () => {
    // Legacy function - now handled by fetchCompanyAndThemeData
    // Keeping for backward compatibility if needed
  };

  const setAppSettings = () => {
    // Legacy function - now handled by fetchAppSettings
    // Keeping for backward compatibility if needed
  };

  const setGraphics = useCallback((graphicsImage) => {
    if (graphicsImage) {
      const imgUrl = `${process.env.BASEURL}${graphicsImage}`;
      setBackgroundImage(encodeURI(imgUrl));
    } else {
      setBackgroundImage("");
    }
  }, []);

  const globalStylesConfig = React.useMemo(
    () => ({
      ":root": {
        "--mui-palette-primary-main": primaryColor,
        "--mui-palette-primary-main-semi": primaryColor + "80",
      },
      "@keyframes breathe": {
        "0%, 100%": {
          transform: "scale(1)",
          opacity: 0.8,
        },
        "50%": {
          transform: "scale(1.05)",
          opacity: 1,
        },
      },
      "@keyframes float": {
        "0%, 100%": {
          transform: "translateY(0px)",
        },
        "50%": {
          transform: "translateY(-10px)",
        },
      },
      ...globalStyles.globalStyle,
    }),
    [primaryColor]
  );

  return (
    <ThemeProvider theme={darkMode ? dynamicThemes.dark : dynamicThemes.light}>
      <SnackbarProvider>
        <AuthContext.Provider
          value={{
            state,
            dispatch,
          }}
        >
          <CssBaseline />
          {/* <GlobalStyles styles={globalStyles.globalStyle} /> */}
          <GlobalStyles styles={globalStylesConfig} />
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  width: "120px",
                  height: "120px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  animation: "float 3s ease-in-out infinite",
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                }}
              >
                <CircularProgress
                  size={80}
                  thickness={3}
                  sx={{
                    color: primaryColor,
                    animation: "breathe 2s ease-in-out infinite",
                  }}
                />
              </Box>
              {retrying && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    color: "text.secondary",
                    textAlign: "center",
                  }}
                >
                  Retrying connection...
                </Typography>
              )}
              {appError && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    color: "error.main",
                    textAlign: "center",
                  }}
                >
                  Connection issues detected. Continuing with cached data...
                </Typography>
              )}
            </Box>
          ) : !okSetup ? (
            <PreSetup />
          ) : (
            <Box>
              <CartTransferManager />
              {/* <AuthDebugger /> */}
              {backgroundImage && (
                <Box
                  component="img"
                  src={backgroundImage}
                  alt="background"
                  sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    zIndex: -1,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: 0.2,
                    loading: "lazy",
                    pointerEvents: "none",
                  }}
                />
              )}
              <AppRoutes data-testid="Eco-Presence" />
            </Box>
          )}
        </AuthContext.Provider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}
